import { useCurrentUser } from '@artic/api-client';
import { AUTH_COOKIE, readCookie } from '@artic/ui/lib/auth-cookies';
import { Socket } from 'phoenix';
import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';

import { getRealtimeUrl } from './realtime-url';

interface RealtimeContextValue {
  /**
   * Returns the live Socket, lazily creating + connecting it on first call.
   * Returns `null` when unauthenticated (no token). Ref-counted: pair every
   * `acquireSocket()` with a `releaseSocket()`.
   */
  acquireSocket: () => Socket | null;
  /** Decrements the consumer ref-count; disconnects the socket at zero. */
  releaseSocket: () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * App-wide realtime socket owner. Mounts inside `AuthProvider`, but opens no
 * connection until a channel consumer (e.g. `useProjectChannel`) acquires the
 * socket — so pages that don't use realtime pay nothing.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  // Asserts we're inside AuthProvider (where this belongs) and scopes the
  // provider to an authenticated session. The socket reads the raw JWT from
  // the cookie, not the parsed user.
  useCurrentUser();

  const socketRef = useRef<Socket | null>(null);
  const refCountRef = useRef(0);

  const acquireSocket = useCallback((): Socket | null => {
    if (!readCookie(AUTH_COOKIE)) return null;

    if (!socketRef.current) {
      const socket = new Socket(getRealtimeUrl(), {
        // A function is re-evaluated on every (re)connect, so a refreshed
        // artic_token cookie is picked up automatically on reconnect.
        params: () => ({ token: readCookie(AUTH_COOKIE) }),
      });
      socket.connect();
      socketRef.current = socket;
    }

    refCountRef.current += 1;
    return socketRef.current;
  }, []);

  const releaseSocket = useCallback(() => {
    refCountRef.current = Math.max(0, refCountRef.current - 1);
    if (refCountRef.current === 0 && socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const value = useMemo<RealtimeContextValue>(
    () => ({ acquireSocket, releaseSocket }),
    [acquireSocket, releaseSocket],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeSocket(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtimeSocket must be used within a <RealtimeProvider>');
  }
  return ctx;
}
